import { Editor } from 'slate';

import type { AlineDescendant, BaseEdit } from '../internal';
import { isParagraphElement } from '../internal';

interface AuthorTrackedChangeOptions {
  authorId: string;
  // ISO timestamp; edits last touched before it belong to an earlier round.
  since: string;
}

/**
 * How many tracked changes one author made or touched since a moment, such as when a reviewer was
 * asked to review. An edit with no timestamp predates timestamps and counts as earlier work.
 */
export function getAuthorTrackedChangeCount(
  editor: Editor,
  options: AuthorTrackedChangeOptions
): number {
  const since = Date.parse(options.since);
  const getIsAuthorsEdit = (edit: Pick<BaseEdit, 'authorId' | 'createdAt' | 'updatedAt'>) => {
    const touchedAt = edit.updatedAt ?? edit.createdAt;
    if (edit.authorId !== options.authorId || !touchedAt) return false;
    return Date.parse(touchedAt) >= since;
  };

  let count = 0;
  for (const [node] of Editor.nodes<AlineDescendant>(editor, {
    at: [],
    match: node => !Editor.isEditor(node),
    voids: true,
  })) {
    count += (node.suggestedEdits ?? []).filter(getIsAuthorsEdit).length;
    // Paragraph split/merge suggestions live on glyphProps, not node.suggestedEdits.
    if (isParagraphElement(node))
      count += (node.glyphProps?.suggestedEdits ?? []).filter(getIsAuthorsEdit).length;
  }
  return count;
}
